import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import SectionOverlay, { InspectorCard, PopupEditModal } from "./components/SectionOverlay";
import type { InlineField } from "./components/SectionOverlay";
import HeroSection from "@/pages/home/components/HeroSection";
import FeaturesSection from "@/pages/home/components/FeaturesSection";
import ProjectsPreview from "@/pages/home/components/ProjectsPreview";
import StrategicPartners from "@/pages/home/components/StrategicPartners";
import TestimonialSection from "@/pages/home/components/TestimonialSection";
import CTASection from "@/pages/home/components/CTASection";
import ContactSection from "@/pages/home/components/ContactSection";
import Footer from "@/components/feature/Footer";
import AboutHero from "@/pages/hakkimizda/components/AboutHero";
import AboutContent from "@/pages/hakkimizda/components/AboutContent";
import ProjectsHero from "@/pages/projelerimiz/components/ProjectsHero";
import PartnersHero from "@/pages/stratejik-ortaklarimiz/components/PartnersHero";
import WhyPartners from "@/pages/stratejik-ortaklarimiz/components/WhyPartners";
import { siteData } from "@/store/siteData";
import { useImageUrl } from "@/hooks/useImageUrl";
import { getFeatureVisibility } from "@/pages/dev-panel/page";

// ── Premium Locked Page ───────────────────────────────────────────────────────

function InspectorLockedPage() {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      icon: "ri-cursor-line",
      title: "Canlı Hover Düzenleme",
      color: "#10B981",
      badge: "Temel Özellik",
      description: "Sitenin herhangi bir bölümünün üzerine fareyi getirdiğinizde anında renkli bir çerçeve belirir ve düzenleme butonları ortaya çıkar. Kod yazmadan, sayfadan ayrılmadan.",
      details: ["Hero, Özellikler, CTA, Footer dahil tüm bölümler", "Renk kodlu bölüm etiketleri ve boyut göstergesi", "Son değişiklik tarihi anlık görüntüleme", "Mobil ve masaüstü uyumlu hover sistemi"],
      mockup: "hero",
    },
    {
      icon: "ri-pencil-fill",
      title: "Inline Metin Editörü",
      color: "#60A5FA",
      badge: "En Çok Kullanılan",
      description: "Başlıklar, açıklamalar, buton yazıları, istatistikler — sayfadan çıkmadan popup modal içinde anında düzenleyin ve kaydedin.",
      details: ["Her bölümde 3-12 arası düzenlenebilir alan", "Ctrl+S ile hızlı kaydetme", "Ctrl+Z ile kaydetme noktasına geri alma", "Karakter sayacı ve alan filtreleme"],
      mockup: "popup",
    },
    {
      icon: "ri-building-2-line",
      title: "Proje Kartı Editörü",
      color: "#F59E0B",
      badge: "Proje Yönetimi",
      description: "Her proje kartı kendi inline editörüne sahip. İsim, konum, açıklama, özellikler, YouTube linki dahil 12 alan tek ekranda düzenlenebilir.",
      details: ["12 düzenlenebilir alan (isim, konum, yıl, alan...)", "İçerik doluluk yüzdesi çubuğu", "YouTube video bağlantısı direkt düzenleme", "Arşivlenmiş projeleri görüntüleme ve düzenleme"],
      mockup: "project",
    },
    {
      icon: "ri-links-line",
      title: "Ortak Kartı Editörü",
      color: "#F43F5E",
      badge: "Partner Yönetimi",
      description: "Stratejik ortak kartlarını sayfadan ayrılmadan düzenleyin. Sektör, kuruluş yılı, kısa açıklama, etiketler ve proje sayısı güncellenir.",
      details: ["11 düzenlenebilir alan per ortak", "Sektör, yıl, açıklama, etiket düzenleme", "Website linki yönetimi", "Tamamlanmamış içerik uyarısı"],
      mockup: "partner",
    },
    {
      icon: "ri-history-line",
      title: "Canlı İstatistik Çubuğu",
      color: "#A78BFA",
      badge: "Analitik",
      description: "Sayfanın üstündeki stats bar sayfa bazında toplam bölüm, inline düzenlenebilir alan, proje ve ortak sayısını canlı gösterir.",
      details: ["Sayfa bazında bölüm sayacı", "Son değişiklik ani gösterge", "Inline düzenlenebilir bölüm sayısı", "Proje ve ortak toplam sayısı"],
      mockup: "stats",
    },
    {
      icon: "ri-layout-grid-line",
      title: "Bölüm Listesi Paneli",
      color: "#34D399",
      badge: "Navigasyon",
      description: "Sol panel tüm sayfa bölümlerini listeler. Her bölümün boyutu, düzenleme türü ve son değişiklik tarihi görüntülenir. Tıklayınca admin editörüne yönlendirir.",
      details: ["Ana Sayfa, Hakkımızda, Projeler, Ortaklar sayfaları", "Bölüm türü renk kodları (Inline / Tam Editör)", "Son değişiklik tarihi etiketleri", "Düzenlenebilir alan sayısı badge"],
      mockup: "panel",
    },
  ];

  useEffect(() => {
    const t = setInterval(() => setActiveFeature(p => (p + 1) % features.length), 4000);
    return () => clearInterval(t);
  }, [features.length]);

  return (
    <div className="min-h-screen bg-[#0A1020] flex flex-col">
      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-[#0F1729]/95 backdrop-blur border-b border-[#1E2B4A] h-14 flex items-center px-5 gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500/20 border border-amber-500/30">
            <i className="ri-lock-fill text-amber-400 text-sm"></i>
          </div>
          <span className="text-white font-semibold text-sm">Görsel İnspektör</span>
          <span className="px-2 py-0.5 bg-amber-500/15 border border-amber-500/30 rounded-full text-amber-400 text-xs font-semibold">Premium</span>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={() => navigate("/kontrol-paneli")}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1E2B4A] text-[#7B8FAF] hover:text-white rounded-lg text-xs cursor-pointer transition-colors whitespace-nowrap border border-[#2B3654]"
          >
            <i className="ri-arrow-left-line"></i>
            Panele Dön
          </button>
          <Link to="/" className="w-8 h-8 flex items-center justify-center text-[#7B8FAF] hover:text-white cursor-pointer rounded-lg hover:bg-white/5 transition-colors">
            <i className="ri-close-line text-lg"></i>
          </Link>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Hero Section */}
        <div className="relative px-6 py-20 text-center overflow-hidden" style={{ background: "linear-gradient(135deg, #0A1020 0%, #0F1729 50%, #0A1020 100%)" }}>
          {/* Decorative grid */}
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: "linear-gradient(rgba(84,102,142,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(84,102,142,0.5) 1px, transparent 1px)",
            backgroundSize: "40px 40px"
          }}></div>
          {/* Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full opacity-20 blur-[100px]" style={{ background: "radial-gradient(circle, #3B82F6, transparent)" }}></div>

          <div className="relative z-10 max-w-3xl mx-auto">
            {/* Lock badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/15 border border-amber-500/30 mb-8">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-vip-crown-fill text-amber-400 text-base"></i>
              </div>
              <span className="text-amber-300 text-sm font-semibold">Premium Özellik — Aktifleştirme Gerekiyor</span>
            </div>

            <h1 className="text-4xl lg:text-5xl font-black text-white mb-5 leading-tight">
              Görsel İnspektör ile<br />
              <span style={{ background: "linear-gradient(135deg, #10B981, #34D399)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Sitenizi Canlı Düzenleyin</span>
            </h1>
            <p className="text-[#7B8FAF] text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
              Sitenizin her bölümünü fare ile hover ederek anında düzenleyin — kod yazmadan, yeniden yüklemeden. Başlıklar, açıklamalar, butonlar, proje kartları, ortak bilgileri hepsi tek tıkla.
            </p>

            {/* Stats */}
            <div className="flex items-center justify-center gap-8 flex-wrap mb-10">
              {[
                { value: "6+", label: "Düzenlenebilir Sayfa", color: "#10B981" },
                { value: "50+", label: "Inline Alan", color: "#F59E0B" },
                { value: "0", label: "Kod Gerektirmez", color: "#60A5FA" },
                { value: "∞", label: "Geri Alma Hakkı", color: "#A78BFA" },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <p className="text-3xl font-black" style={{ color: s.color }}>{s.value}</p>
                  <p className="text-[#7B8FAF] text-xs mt-1">{s.label}</p>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <div className="flex items-center gap-3 px-6 py-3.5 rounded-xl bg-amber-500/15 border border-amber-500/30">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500/20">
                  <i className="ri-shield-keyhole-line text-amber-400 text-base"></i>
                </div>
                <div className="text-left">
                  <p className="text-amber-300 text-sm font-semibold">Aktifleştirmek için</p>
                  <p className="text-amber-400/70 text-xs">Geliştiriciye başvurun → Geliştirici Paneli → Özellik Yönetimi</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature showcase */}
        <div className="px-6 py-16 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#3B82F6] text-xs uppercase tracking-widest font-semibold mb-3">Özellikler</p>
            <h2 className="text-2xl font-bold text-white">Ne Yapabilirsiniz?</h2>
            <p className="text-[#7B8FAF] text-sm mt-2">Görsel İnpektör&apos;ün tüm yetenekleri</p>
          </div>

          {/* Feature tabs + detail */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
            {/* Left: feature list */}
            <div className="space-y-2">
              {features.map((f, i) => (
                <button
                  key={i}
                  onClick={() => setActiveFeature(i)}
                  className={`w-full flex items-start gap-4 p-4 rounded-xl text-left cursor-pointer transition-all border ${
                    activeFeature === i
                      ? "border-[#2B3654] bg-[#141E35]"
                      : "border-transparent bg-transparent hover:bg-[#141E35]/50"
                  }`}
                >
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-colors"
                    style={{ background: activeFeature === i ? f.color + "25" : "#1E2B4A" }}
                  >
                    <i className={`${f.icon} text-lg`} style={{ color: activeFeature === i ? f.color : "#7B8FAF" }}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className={`text-sm font-semibold ${activeFeature === i ? "text-white" : "text-[#7B8FAF]"}`}>{f.title}</p>
                      {activeFeature === i && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap" style={{ background: f.color + "25", color: f.color }}>{f.badge}</span>
                      )}
                    </div>
                    <p className="text-[#5E7398] text-xs leading-relaxed line-clamp-2">{f.description}</p>
                  </div>
                  {activeFeature === i && (
                    <div className="w-1.5 h-full min-h-[40px] rounded-full flex-shrink-0" style={{ background: f.color }}></div>
                  )}
                </button>
              ))}
            </div>

            {/* Right: detail card */}
            <div className="bg-[#141E35] border border-[#2B3654] rounded-2xl p-6 flex flex-col">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: features[activeFeature].color + "25" }}
                >
                  <i className={`${features[activeFeature].icon} text-2xl`} style={{ color: features[activeFeature].color }}></i>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <h3 className="text-white font-bold text-base">{features[activeFeature].title}</h3>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: features[activeFeature].color + "25", color: features[activeFeature].color }}>
                      {features[activeFeature].badge}
                    </span>
                  </div>
                  <p className="text-[#7B8FAF] text-xs">{features[activeFeature].description}</p>
                </div>
              </div>

              <div className="flex-1 space-y-2 mt-2">
                <p className="text-[#5E7398] text-xs uppercase tracking-widest font-semibold mb-3">Detaylar</p>
                {features[activeFeature].details.map((d, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <div
                      className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0"
                      style={{ background: features[activeFeature].color + "20" }}
                    >
                      <i className="ri-check-line text-xs" style={{ color: features[activeFeature].color }}></i>
                    </div>
                    <p className="text-[#A5B5CC] text-sm leading-relaxed">{d}</p>
                  </div>
                ))}
              </div>

              {/* Animated mock indicator */}
              <div className="mt-5 pt-4 border-t border-[#2B3654]">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: features[activeFeature].color }}></div>
                  <p className="text-[#5E7398] text-xs">Aktif olsaydı şu an bu özelliği kullanabilirdiniz</p>
                </div>
              </div>
            </div>
          </div>

          {/* Feature grid — all features at a glance */}
          <div className="mb-12">
            <h3 className="text-white font-bold text-lg mb-5 text-center">Tüm Yetenekler Bir Bakışta</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: "ri-cursor-line",       color: "#10B981", title: "Hover Overlay",           desc: "Her bölüme hover et, anlık düzenle" },
                { icon: "ri-pencil-fill",        color: "#60A5FA", title: "Popup Metin Editörü",     desc: "Yazıları popup modal'da değiştir" },
                { icon: "ri-arrow-go-back-line", color: "#F59E0B", title: "Geri Alma Sistemi",       desc: "Ctrl+Z ile kaydetme noktasına dön" },
                { icon: "ri-building-2-line",    color: "#F43F5E", title: "Proje Kartı Editörü",     desc: "12 alanı tek ekranda düzenle" },
                { icon: "ri-links-line",         color: "#A78BFA", title: "Ortak Kartı Editörü",     desc: "Ortak bilgilerini canlı güncelle" },
                { icon: "ri-bar-chart-fill",     color: "#34D399", title: "Doluluk Çubuğu",          desc: "Her kartın içerik doluluk yüzdesini gösterir." },
                { icon: "ri-layout-grid-line",   color: "#FB923C", title: "Bölüm Listesi Paneli",    desc: "Tüm bölümleri soldaki panelden yönet" },
                { icon: "ri-bar-chart-line",     color: "#38BDF8", title: "İstatistik Çubuğu",       desc: "Canlı proje, ortak ve değişiklik sayacı" },
                { icon: "ri-keyboard-line",      color: "#E879F9", title: "Klavye Kısayolları",      desc: "Ctrl+S kaydet, Ctrl+Z geri al, Esc çık" },
                { icon: "ri-pages-line",         color: "#FBBF24", title: "4 Sayfa Desteği",         desc: "Ana, Hakkımızda, Projeler, Ortaklar" },
                { icon: "ri-time-line",          color: "#6EE7B7", title: "Son Değişiklik",          desc: "Her bölümün son edit tarihini göster" },
                { icon: "ri-settings-3-line",    color: "#94A3B8", title: "Tam Editör Bağlantısı",   desc: "Tek tık ile admin paneline geç" },
              ].map((item, i) => (
                <div key={i} className="bg-[#141E35] border border-[#2B3654] rounded-xl p-4 flex items-start gap-3 hover:border-[#2B3654]/80 transition-colors">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: item.color + "20" }}>
                    <i className={`${item.icon} text-base`} style={{ color: item.color }}></i>
                  </div>
                  <div>
                    <p className="text-white text-xs font-semibold mb-0.5">{item.title}</p>
                    <p className="text-[#5E7398] text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* How it works — step by step */}
          <div className="bg-[#0F1729] border border-[#2B3654] rounded-2xl p-8 mb-12">
            <h3 className="text-white font-bold text-lg mb-7 text-center">Nasıl Çalışır?</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { step: "01", icon: "ri-cursor-line",     color: "#10B981", title: "Bölüme Git",         desc: "Düzenlemek istediğiniz sayfa bölümünün üzerine fareyi götürün" },
                { step: "02", icon: "ri-palette-line",    color: "#60A5FA", title: "Çerçeve Belirir",    desc: "Renkli bir çerçeve ve düzenleme butonları otomatik görünür" },
                { step: "03", icon: "ri-pencil-fill",     color: "#F59E0B", title: "Düzenle Butonuna Tıkla", desc: "Yeşil 'Düzenle' butonu popup editörü açar, metinleri değiştirin" },
                { step: "04", icon: "ri-check-double-line", color: "#F43F5E", title: "Kaydet",           desc: "Ctrl+S veya Kaydet butonuyla değişiklik anında siteye yansır" },
              ].map((s, i) => (
                <div key={i} className="text-center relative">
                  {i < 3 && (
                    <div className="hidden md:block absolute top-6 left-full w-full h-px bg-[#2B3654] -translate-y-px z-0" style={{ width: "calc(100% - 40px)", left: "calc(50% + 24px)" }}></div>
                  )}
                  <div className="relative z-10">
                    <div className="w-14 h-14 flex items-center justify-center rounded-2xl mx-auto mb-4 relative" style={{ background: s.color + "20", border: `2px solid ${s.color}40` }}>
                      <i className={`${s.icon} text-2xl`} style={{ color: s.color }}></i>
                      <span className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center rounded-full text-[9px] font-black" style={{ background: s.color, color: "#fff" }}>{s.step}</span>
                    </div>
                    <p className="text-white text-sm font-semibold mb-1">{s.title}</p>
                    <p className="text-[#5E7398] text-xs leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA bottom */}
          <div className="bg-amber-500/10 border border-amber-500/25 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-amber-500/20 border border-amber-500/30 mx-auto mb-5">
              <i className="ri-lock-fill text-amber-400 text-3xl"></i>
            </div>
            <h3 className="text-white font-black text-xl mb-3">Bu özelliği aktifleştirmek ister misiniz?</h3>
            <p className="text-[#A5B5CC] text-sm mb-6 max-w-lg mx-auto">
              Görsel İnpektör, geliştirici tarafından aktifleştirilmesi gereken bir premium özelliktir. Geliştiricinizle iletişime geçerek etkinleştirebilirsiniz.
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <div className="flex items-center gap-2 bg-amber-500/15 border border-amber-500/30 rounded-xl px-5 py-3">
                <i className="ri-information-line text-amber-400"></i>
                <p className="text-amber-300 text-sm font-medium">Geliştirici Paneli → Özellik Yönetimi → &quot;Görsel İnpektör&quot; → Aktif Et</p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 mt-5 flex-wrap">
              <button
                onClick={() => navigate("/kontrol-paneli")}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#1E2B4A] border border-[#2B3654] text-[#7B8FAF] hover:text-white rounded-xl text-sm cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Kontrol Paneline Dön
              </button>
              <Link
                to="/"
                className="flex items-center gap-2 px-6 py-2.5 bg-[#1E2B4A] border border-[#2B3654] text-[#7B8FAF] hover:text-white rounded-xl text-sm cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-home-line"></i>
                Siteye Dön
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

type InspectorPage = "home" | "about" | "projects" | "partners";

interface SectionConfig {
  id: string;
  label: string;
  description: string;
  tab: string;
  color: { border: string; bg: string; badge: string; text: string };
  icon: string;
  fieldCount?: number;
  sizeHint?: string;
  inlineFields?: () => InlineField[];
  onInlineSave?: (fields: Record<string, string>) => Promise<void>;
}

const colors = {
  home:     { border: "#10B981", bg: "rgba(16,185,129,0.07)", badge: "#059669", text: "#fff" },
  projects: { border: "#F59E0B", bg: "rgba(245,158,11,0.07)", badge: "#D97706", text: "#fff" },
  partners: { border: "#F43F5E", bg: "rgba(244,63,94,0.07)",  badge: "#E11D48", text: "#fff" },
  about:    { border: "#14B8A6", bg: "rgba(20,184,166,0.07)", badge: "#0D9488", text: "#fff" },
  messages: { border: "#F97316", bg: "rgba(249,115,22,0.07)", badge: "#EA580C", text: "#fff" },
  settings: { border: "#94A3B8", bg: "rgba(148,163,184,0.07)", badge: "#475569", text: "#fff" },
};

// ── Inline save helpers ───────────────────────────────────────────────────────

function heroInlineFields(): InlineField[] {
  const d = siteData.getHero();
  return [
    { key: "tagline",   label: "Tagline (üst etiket)",  value: d.tagline },
    { key: "title",     label: "Ana Başlık",             value: d.title },
    { key: "subtitle",  label: "Alt Başlık",             value: d.subtitle, multiline: true },
    { key: "cta1Text",  label: "Buton 1 Yazısı",        value: d.cta1Text },
    { key: "cta2Text",  label: "Buton 2 Yazısı",        value: d.cta2Text },
    { key: "stat1val",  label: "İstatistik 1 Değer",    value: d.stats[0]?.value || "" },
    { key: "stat1lbl",  label: "İstatistik 1 Etiket",   value: d.stats[0]?.label || "" },
    { key: "stat2val",  label: "İstatistik 2 Değer",    value: d.stats[1]?.value || "" },
    { key: "stat2lbl",  label: "İstatistik 2 Etiket",   value: d.stats[1]?.label || "" },
    { key: "stat3val",  label: "İstatistik 3 Değer",    value: d.stats[2]?.value || "" },
    { key: "stat3lbl",  label: "İstatistik 3 Etiket",   value: d.stats[2]?.label || "" },
  ];
}

async function heroInlineSave(fields: Record<string, string>) {
  const d = siteData.getHero();
  const updated = {
    ...d,
    tagline: fields.tagline ?? d.tagline,
    title: fields.title ?? d.title,
    subtitle: fields.subtitle ?? d.subtitle,
    cta1Text: fields.cta1Text ?? d.cta1Text,
    cta2Text: fields.cta2Text ?? d.cta2Text,
    stats: [
      { ...d.stats[0], value: fields.stat1val ?? d.stats[0].value, label: fields.stat1lbl ?? d.stats[0].label },
      { ...d.stats[1], value: fields.stat2val ?? d.stats[1].value, label: fields.stat2lbl ?? d.stats[1].label },
      { ...d.stats[2], value: fields.stat3val ?? d.stats[2].value, label: fields.stat3lbl ?? d.stats[2].label },
    ],
  };
  await siteData.saveHero(updated);
}

function featuresInlineFields(): InlineField[] {
  const d = siteData.getFeatures();
  const fields: InlineField[] = [];
  d.forEach((f, i) => {
    fields.push(
      { key: `f${i}_title`, label: `Özellik ${i+1} Başlık`, value: f.title },
      { key: `f${i}_desc`,  label: `Özellik ${i+1} Açıklama`, value: f.desc, multiline: true },
    );
  });
  return fields;
}

async function featuresInlineSave(fields: Record<string, string>) {
  const d = siteData.getFeatures();
  const updated = d.map((f, i) => ({
    ...f,
    title: fields[`f${i}_title`] ?? f.title,
    desc:  fields[`f${i}_desc`]  ?? f.desc,
  }));
  await siteData.saveFeatures(updated);
}

function ctaInlineFields(): InlineField[] {
  const d = siteData.getCTA();
  return [
    { key: "tag",      label: "Üst Etiket",     value: d.tag },
    { key: "title",    label: "Başlık",          value: d.title },
    { key: "subtitle", label: "Alt Başlık",      value: d.subtitle, multiline: true },
    { key: "cta1Text", label: "Birincil Buton",  value: d.cta1Text },
    { key: "cta2Text", label: "İkincil Buton",   value: d.cta2Text },
    { key: "trust0",   label: "Güven Metni 1",   value: d.trustItems[0]?.text || "" },
    { key: "trust1",   label: "Güven Metni 2",   value: d.trustItems[1]?.text || "" },
    { key: "trust2",   label: "Güven Metni 3",   value: d.trustItems[2]?.text || "" },
    { key: "trust3",   label: "Güven Metni 4",   value: d.trustItems[3]?.text || "" },
  ];
}

async function ctaInlineSave(fields: Record<string, string>) {
  const d = siteData.getCTA();
  await siteData.saveCTA({
    ...d,
    tag: fields.tag ?? d.tag,
    title: fields.title ?? d.title,
    subtitle: fields.subtitle ?? d.subtitle,
    cta1Text: fields.cta1Text ?? d.cta1Text,
    cta2Text: fields.cta2Text ?? d.cta2Text,
    trustItems: d.trustItems.map((t, i) => ({ ...t, text: fields[`trust${i}`] ?? t.text })),
  });
}

function testimonialInlineFields(): InlineField[] {
  const d = siteData.getTestimonials();
  const fields: InlineField[] = [];
  d.forEach((t, i) => {
    fields.push(
      { key: `t${i}_quote`,   label: `Referans ${i+1} Alıntı`,   value: t.quote, multiline: true },
      { key: `t${i}_name`,    label: `Referans ${i+1} İsim`,     value: t.name },
      { key: `t${i}_title`,   label: `Referans ${i+1} Unvan`,    value: t.title },
      { key: `t${i}_company`, label: `Referans ${i+1} Şirket`,   value: t.company },
    );
  });
  return fields;
}

async function testimonialInlineSave(fields: Record<string, string>) {
  const d = siteData.getTestimonials();
  const updated = d.map((t, i) => ({
    ...t,
    quote:   fields[`t${i}_quote`]   ?? t.quote,
    name:    fields[`t${i}_name`]    ?? t.name,
    title:   fields[`t${i}_title`]   ?? t.title,
    company: fields[`t${i}_company`] ?? t.company,
  }));
  await siteData.saveTestimonials(updated);
}

function aboutHeroInlineFields(): InlineField[] {
  const d = siteData.getAbout();
  return [
    { key: "heroTagline",  label: "Tagline",       value: d.heroTagline },
    { key: "heroTitle",    label: "Başlık",        value: d.heroTitle, multiline: true },
    { key: "heroSubtitle", label: "Alt Başlık",    value: d.heroSubtitle, multiline: true },
  ];
}

async function aboutHeroInlineSave(fields: Record<string, string>) {
  const d = siteData.getAbout();
  await siteData.saveAbout({
    ...d,
    heroTagline: fields.heroTagline ?? d.heroTagline,
    heroTitle:   fields.heroTitle   ?? d.heroTitle,
    heroSubtitle: fields.heroSubtitle ?? d.heroSubtitle,
  });
}

function aboutContentInlineFields(): InlineField[] {
  const d = siteData.getAbout();
  return [
    { key: "visionTagline", label: "Vizyon Tagline",   value: d.visionTagline },
    { key: "visionTitle",   label: "Vizyon Başlık",    value: d.visionTitle, multiline: true },
    { key: "visionText1",   label: "Vizyon Paragraf 1", value: d.visionText1, multiline: true },
    { key: "visionText2",   label: "Vizyon Paragraf 2", value: d.visionText2, multiline: true },
    { key: "floatStatValue", label: "Yüzen İstatistik Değer", value: d.floatStatValue },
    { key: "floatStatLabel", label: "Yüzen İstatistik Etiket", value: d.floatStatLabel },
    { key: "valuesTagline", label: "Değerler Tagline",  value: d.valuesTagline },
    { key: "valuesTitle",   label: "Değerler Başlık",   value: d.valuesTitle },
    { key: "timelineTagline", label: "Zaman Çizelgesi Tagline", value: d.timelineTagline },
    { key: "timelineTitle", label: "Zaman Çizelgesi Başlık", value: d.timelineTitle },
    { key: "teamTitle",     label: "Ekip Başlık",        value: d.teamTitle },
    ...d.values.map((v, i) => ({ key: `val${i}_title`, label: `Değer ${i+1} Başlık`, value: v.title })),
    ...d.values.map((v, i) => ({ key: `val${i}_desc`,  label: `Değer ${i+1} Açıklama`, value: v.desc, multiline: true })),
  ];
}

async function aboutContentInlineSave(fields: Record<string, string>) {
  const d = siteData.getAbout();
  await siteData.saveAbout({
    ...d,
    visionTagline: fields.visionTagline ?? d.visionTagline,
    visionTitle:   fields.visionTitle   ?? d.visionTitle,
    visionText1:   fields.visionText1   ?? d.visionText1,
    visionText2:   fields.visionText2   ?? d.visionText2,
    floatStatValue: fields.floatStatValue ?? d.floatStatValue,
    floatStatLabel: fields.floatStatLabel ?? d.floatStatLabel,
    valuesTagline: fields.valuesTagline ?? d.valuesTagline,
    valuesTitle:   fields.valuesTitle   ?? d.valuesTitle,
    timelineTagline: fields.timelineTagline ?? d.timelineTagline,
    timelineTitle:   fields.timelineTitle   ?? d.timelineTitle,
    teamTitle:     fields.teamTitle     ?? d.teamTitle,
    values: d.values.map((v, i) => ({
      ...v,
      title: fields[`val${i}_title`] ?? v.title,
      desc:  fields[`val${i}_desc`]  ?? v.desc,
    })),
  });
}

function partnersHeroInlineFields(): InlineField[] {
  const partners = siteData.getPartners();
  // Partners sayfasının hero bölümü about data'dan gelmiyor, static - about hero gibi
  const d = siteData.getAbout();
  return [
    { key: "heroTagline",  label: "Tagline",     value: d.heroTagline },
    { key: "heroTitle",    label: "Başlık",      value: d.heroTitle, multiline: true },
    { key: "heroSubtitle", label: "Alt Başlık",  value: d.heroSubtitle, multiline: true },
  ];
}

async function partnersHeroInlineSave(fields: Record<string, string>) {
  const d = siteData.getAbout();
  await siteData.saveAbout({
    ...d,
    heroTagline:  fields.heroTagline  ?? d.heroTagline,
    heroTitle:    fields.heroTitle    ?? d.heroTitle,
    heroSubtitle: fields.heroSubtitle ?? d.heroSubtitle,
  });
}

// ── Page configs ──────────────────────────────────────────────────────────────

function buildPageConfigs(): Record<InspectorPage, SectionConfig[]> {
  return {
    home: [
      {
        id: "hero", label: "Hero Bölümü", description: "Ana başlık, istatistikler, butonlar",
        tab: "homepage", color: colors.home, icon: "ri-home-4-line", sizeHint: "Full Screen",
        inlineFields: heroInlineFields, onInlineSave: heroInlineSave,
      },
      {
        id: "features", label: "Özellikler", description: "3 özellik kartı ve içerikleri",
        tab: "homepage", color: colors.home, icon: "ri-layout-grid-line", sizeHint: "Büyük",
        inlineFields: featuresInlineFields, onInlineSave: featuresInlineSave,
      },
      {
        id: "projects-p", label: "Projeler Önizleme", description: "Öne çıkan proje kartları",
        tab: "projects", color: colors.projects, icon: "ri-building-2-line", sizeHint: "Orta",
      },
      {
        id: "partners-s", label: "Stratejik Ortaklar", description: "Ortak şirketler bölümü",
        tab: "partners", color: colors.partners, icon: "ri-links-line", sizeHint: "Orta",
      },
      {
        id: "testimon", label: "Referanslar", description: "Müşteri yorumları ve fotoğraflar",
        tab: "homepage", color: colors.home, icon: "ri-chat-quote-line", sizeHint: "Orta",
        inlineFields: testimonialInlineFields, onInlineSave: testimonialInlineSave,
      },
      {
        id: "cta", label: "CTA Bölümü", description: "Çağrı aksiyonu, güven göstergeleri",
        tab: "homepage", color: colors.home, icon: "ri-megaphone-line", sizeHint: "Küçük",
        inlineFields: ctaInlineFields, onInlineSave: ctaInlineSave,
      },
      {
        id: "contact", label: "İletişim Formu", description: "İletişim formu ve adres",
        tab: "messages", color: colors.messages, icon: "ri-mail-send-line", sizeHint: "Büyük",
      },
      {
        id: "footer", label: "Alt Bilgi (Footer)", description: "Linkler, telif hakkı, sosyal medya",
        tab: "settings", color: colors.settings, icon: "ri-layout-bottom-line", sizeHint: "Küçük",
      },
    ],
    about: [
      {
        id: "about-hero", label: "Hero Alanı", description: "Başlık, tagline, arka plan görseli",
        tab: "about", color: colors.about, icon: "ri-image-line", sizeHint: "Full Screen",
        inlineFields: aboutHeroInlineFields, onInlineSave: aboutHeroInlineSave,
      },
      {
        id: "about-content", label: "Vizyon / Misyon", description: "Paragraflar, istatistik, değerler",
        tab: "about", color: colors.about, icon: "ri-eye-line", sizeHint: "Büyük",
        inlineFields: aboutContentInlineFields, onInlineSave: aboutContentInlineSave,
      },
      {
        id: "about-cta", label: "CTA Bölümü", description: "Çağrı aksiyonu",
        tab: "homepage", color: colors.home, icon: "ri-megaphone-line", sizeHint: "Küçük",
        inlineFields: ctaInlineFields, onInlineSave: ctaInlineSave,
      },
      {
        id: "about-contact", label: "İletişim Formu", description: "İletişim formu",
        tab: "messages", color: colors.messages, icon: "ri-mail-send-line", sizeHint: "Büyük",
      },
    ],
    projects: [
      {
        id: "proj-hero", label: "Projeler Hero", description: "Sayfa başlığı ve üst bölüm",
        tab: "projects", color: colors.projects, icon: "ri-image-line", sizeHint: "Orta",
      },
      {
        id: "proj-cards", label: "Proje Kartları", description: "Her kartı ayrı ayrı düzenle",
        tab: "projects", color: colors.projects, icon: "ri-building-2-line", sizeHint: "Dinamik",
      },
    ],
    partners: [
      {
        id: "part-hero", label: "Ortaklar Hero", description: "Hero başlık ve arka plan",
        tab: "partners", color: colors.partners, icon: "ri-image-line", sizeHint: "Orta",
        inlineFields: partnersHeroInlineFields, onInlineSave: partnersHeroInlineSave,
      },
      {
        id: "part-why", label: "Neden Biz", description: "Avantajlar ve değer önerileri",
        tab: "partners", color: colors.partners, icon: "ri-shield-star-line", sizeHint: "Büyük",
      },
      {
        id: "part-cards", label: "Ortak Kartları", description: "Her kartı ayrı ayrı düzenle",
        tab: "partners", color: colors.partners, icon: "ri-links-line", sizeHint: "Dinamik",
      },
    ],
  };
}

const pageTabs: { id: InspectorPage; label: string; icon: string; count: () => number }[] = [
  { id: "home",     label: "Ana Sayfa",  icon: "ri-home-4-line",      count: () => 8 },
  { id: "about",    label: "Hakkımızda", icon: "ri-information-line",  count: () => 4 },
  { id: "projects", label: "Projeler",   icon: "ri-building-2-line",  count: () => siteData.getProjects().length + 1 },
  { id: "partners", label: "Ortaklar",   icon: "ri-links-line",       count: () => siteData.getPartners().length + 2 },
];

// ── Stats Bar ─────────────────────────────────────────────────────────────────

function InspectorStatsBar({ activePage }: { activePage: InspectorPage }) {
  const projects = siteData.getProjects();
  const partners = siteData.getPartners();
  const changelog = siteData.getChangeLog();
  const configs = buildPageConfigs()[activePage];
  const editableCount = configs.filter(c => c.inlineFields).length;

  const stats = [
    { label: "Toplam Bölüm", value: configs.length, icon: "ri-layout-grid-line", color: "#10B981" },
    { label: "Inline Düzenlenebilir", value: editableCount, icon: "ri-pencil-fill", color: "#60A5FA" },
    { label: "Proje", value: projects.length, icon: "ri-building-2-line", color: "#F59E0B" },
    { label: "Ortak", value: partners.length, icon: "ri-links-line", color: "#F43F5E" },
    { label: "Değişiklik", value: changelog.length, icon: "ri-history-line", color: "#94A3B8" },
  ];

  const recentChange = changelog[0];
  const changeTimeAgo = recentChange
    ? (() => {
        const diff = Date.now() - new Date(recentChange.timestamp).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return "Az önce";
        if (mins < 60) return `${mins}dk önce`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}sa önce`;
        return `${Math.floor(hrs / 24)}g önce`;
      })()
    : null;

  return (
    <div className="bg-[#0F1729] border-b border-[#1E2B4A] px-5 py-2.5">
      <div className="flex items-center gap-6 overflow-x-auto">
        {stats.map((s, i) => (
          <div key={i} className="flex items-center gap-2 flex-shrink-0">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: s.color + "20" }}>
              <i className={`${s.icon} text-sm`} style={{ color: s.color }}></i>
            </div>
            <div>
              <p className="text-white text-sm font-bold leading-tight">{s.value}</p>
              <p className="text-[#465886] text-xs leading-tight">{s.label}</p>
            </div>
          </div>
        ))}

        {recentChange && (
          <div className="flex items-center gap-2 ml-auto flex-shrink-0 bg-[#141E35] border border-[#2B3654] rounded-lg px-3 py-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0"></span>
            <div className="flex flex-col">
              <span className="text-[#A5B5CC] text-xs font-medium">{recentChange.section}</span>
              <span className="text-[#465886] text-xs">{changeTimeAgo} · {recentChange.action}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Section List Panel ────────────────────────────────────────────────────────

function SectionListPanel({
  configs,
  activePage,
  onSectionClick,
}: {
  configs: SectionConfig[];
  activePage: InspectorPage;
  onSectionClick: (tab: string) => void;
}) {
  const lastChanged = siteData.getLastChanged();

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" });
  };

  const pageLabel = activePage === "home" ? "Ana Sayfa" : activePage === "about" ? "Hakkımızda" : activePage === "projects" ? "Projeler" : "Ortaklar";

  return (
    <div className="w-72 flex-shrink-0 bg-[#0F1729] border-r border-[#2B3654] overflow-y-auto flex flex-col">
      <div className="p-4 flex-1">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[#7B8FAF] text-xs uppercase tracking-widest font-semibold">{pageLabel} bölümleri</p>
          <span className="text-[#3B82F6] text-xs bg-[#3B82F6]/15 border border-[#3B82F6]/30 px-2 py-0.5 rounded-full">{configs.length}</span>
        </div>
        <div className="space-y-1.5">
          {configs.map((cfg) => {
            const isInline = !!cfg.inlineFields;
            const changed = lastChanged[`yg_${cfg.tab === "homepage" ? "hero" : cfg.tab === "messages" ? "cta" : cfg.tab}`];
            return (
              <button
                key={cfg.id}
                onClick={() => onSectionClick(cfg.tab)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left cursor-pointer hover:bg-white/5 transition-all group border border-transparent hover:border-[#2B3654]"
              >
                <div
                  className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                  style={{ background: cfg.color.badge + "20" }}
                >
                  <i className={`${cfg.icon} text-sm`} style={{ color: cfg.color.border }}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-white text-xs font-medium truncate">{cfg.label}</p>
                    {isInline && (
                      <span className="flex-shrink-0 w-3.5 h-3.5 flex items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400" title="Inline düzenlenebilir">
                        <i className="ri-pencil-fill text-[9px]"></i>
                      </span>
                    )}
                  </div>
                  <p className="text-[#465886] text-xs truncate">{cfg.description}</p>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  {cfg.sizeHint && (
                    <span className="text-[#465886] text-xs">{cfg.sizeHint}</span>
                  )}
                  {changed && (
                    <span className="text-[#465886] text-xs">{formatDate(changed)}</span>
                  )}
                  <i className="ri-arrow-right-line text-[#3B82F6] text-xs opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </div>
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-5 bg-[#141E35] border border-[#2B3654] rounded-lg p-3 space-y-2">
          <p className="text-[#7B8FAF] text-xs font-semibold uppercase tracking-widest mb-2">Düzenleme Türleri</p>
          {[
            { color: "#10B981", label: "Inline Düzenle", sub: "Yazı/metin anında değiştir" },
            { color: "#F59E0B", label: "Tam Editör", sub: "Görsel + tüm alanlar" },
            { color: "#3B82F6", label: "Detaylar", sub: "Bölüm meta bilgisi" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: item.color }}></span>
              <div className="min-w-0">
                <span className="text-[#A5B5CC] text-xs font-medium">{item.label}</span>
                <span className="text-[#465886] text-xs ml-1.5">— {item.sub}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Inline düzenlenebilir bölümler */}
        {configs.some(c => c.inlineFields) && (
          <div className="mt-4 bg-[#141E35] border border-emerald-500/20 rounded-lg p-3">
            <p className="text-emerald-400 text-xs font-semibold uppercase tracking-widest mb-2">
              <i className="ri-pencil-fill mr-1"></i>Inline Düzenlenebilir
            </p>
            <div className="space-y-1">
              {configs.filter(c => c.inlineFields).map(c => (
                <div key={c.id} className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full flex-shrink-0 bg-emerald-500/40"></div>
                  <span className="text-[#7B8FAF] text-xs">{c.label}</span>
                  <span className="text-[#465886] text-xs ml-auto">{c.inlineFields?.().length} alan</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── SectionOverlay with Popup support ───────────────────────────────────────

function EditableSectionOverlay({
  cfg,
  lastChanged,
  children,
}: {
  cfg: SectionConfig;
  lastChanged?: string;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);

  const handleGoAdmin = useCallback(() => navigate(`/kontrol-paneli?tab=${cfg.tab}`), [navigate, cfg.tab]);

  const inlineFields = cfg.inlineFields?.() || [];
  const hasInline = inlineFields.length > 0;
  const histKey = `inspector-section-${cfg.id}`;

  return (
    <>
      <div
        className="relative transition-all duration-200"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => { if (!popupOpen) setHovered(false); }}
      >
        <div style={{ pointerEvents: "none" }}>{children}</div>

        {/* Main hover overlay */}
        <div
          className={`absolute inset-0 z-20 transition-all duration-200 ${hovered && !popupOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          style={{ border: `3px solid ${cfg.color.border}`, background: cfg.color.bg }}
        >
          {/* Top left badge */}
          <div className="absolute top-4 left-4 flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full shadow-lg" style={{ background: cfg.color.badge }}>
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className={`${cfg.icon} text-xs`} style={{ color: cfg.color.text }}></i>
              </div>
              <span className="text-xs font-semibold whitespace-nowrap" style={{ color: cfg.color.text }}>{cfg.label}</span>
            </div>
            {hasInline && (
              <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/30">
                <i className="ri-pencil-fill text-emerald-400 text-xs"></i>
                <span className="text-emerald-300 text-xs font-medium">{inlineFields.length} alan</span>
              </div>
            )}
            {cfg.sizeHint && (
              <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-black/40">
                <span className="text-white text-xs">{cfg.sizeHint}</span>
              </div>
            )}
          </div>

          {lastChanged && (
            <div className="absolute top-4 right-4 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-black/40">
              <i className="ri-time-line text-white/70 text-xs"></i>
              <span className="text-white/70 text-xs whitespace-nowrap">{lastChanged}</span>
            </div>
          )}

          {/* Center buttons */}
          <div className="absolute inset-0 flex items-center justify-center gap-3 flex-wrap">
            {hasInline && (
              <button
                onClick={e => { e.stopPropagation(); setPopupOpen(true); }}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm shadow-xl cursor-pointer transition-all hover:opacity-90 active:scale-95 bg-emerald-600 text-white"
              >
                <i className="ri-pencil-fill text-base"></i>
                <span className="whitespace-nowrap">Düzenle</span>
                <span className="text-emerald-200 text-xs">({inlineFields.length})</span>
              </button>
            )}
            <button
              onClick={handleGoAdmin}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm shadow-xl cursor-pointer transition-all hover:opacity-90 active:scale-95"
              style={{ background: cfg.color.border, color: "#fff" }}
            >
              <i className="ri-settings-3-line text-base"></i>
              <span className="whitespace-nowrap">Tam Editör</span>
              <i className="ri-arrow-right-line text-sm"></i>
            </button>
          </div>

          <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
            <span className="text-xs px-3 py-1 rounded-full" style={{ background: "rgba(0,0,0,0.5)", color: "#e2e8f0" }}>{cfg.description}</span>
          </div>
        </div>
      </div>

      {/* Popup Edit Modal */}
      {hasInline && (
        <PopupEditModal
          isOpen={popupOpen}
          onClose={() => { setPopupOpen(false); setHovered(false); }}
          label={cfg.label}
          icon={cfg.icon}
          borderColor={cfg.color.border}
          fields={inlineFields}
          historyKey={histKey}
          onSave={async (vals) => { if (cfg.onInlineSave) await cfg.onInlineSave(vals); }}
          onGoAdmin={handleGoAdmin}
          isLocked={false}
          lockedUndoCount={3}
        />
      )}
    </>
  );
}

// ── Individual Project Card ───────────────────────────────────────────────────

function InspectorProjectCard({
  project, onUpdated,
}: {
  project: ReturnType<typeof siteData.getProjects>[0];
  onUpdated: () => void;
}) {
  const imgUrl = useImageUrl(project.imageId, project.image);
  const statusTags = siteData.getStatusTags();
  const tagInfo = statusTags.find(t => t.id === project.statusColor);

  const inlineFields: InlineField[] = [
    { key: "name",        label: "Proje Adı",    value: project.name },
    { key: "location",    label: "Konum",        value: project.location, hint: "Şehir, İlçe formatında" },
    { key: "year",        label: "Yıl",          value: project.year },
    { key: "area",        label: "Alan",         value: project.area },
    { key: "description", label: "Açıklama",     value: project.description, multiline: true },
    { key: "units",       label: "Konut Adedi",  value: String(project.units) },
    { key: "type",        label: "Proje Türü",   value: project.type },
    { key: "features0",   label: "Özellik 1",    value: project.features[0] || "" },
    { key: "features1",   label: "Özellik 2",    value: project.features[1] || "" },
    { key: "features2",   label: "Özellik 3",    value: project.features[2] || "" },
    { key: "youtubeUrl",  label: "YouTube URL",  value: project.youtubeUrl || "" },
    { key: "googleMapsUrl", label: "Google Maps URL", value: project.googleMapsUrl || "" },
  ];

  const handleInlineSave = (fields: Record<string, string>) => {
    const all = siteData.getProjects();
    const updated = all.map((p) => p.id === project.id ? {
      ...p, ...fields,
      units: Number(fields.units || p.units),
      features: [
        fields.features0 || p.features[0] || "",
        fields.features1 || p.features[1] || "",
        fields.features2 || p.features[2] || "",
        ...p.features.slice(3),
      ].filter(Boolean),
    } : p);
    siteData.saveProjects(updated, "Inline Düzenlendi", project.name + " - Inspector");
    onUpdated();
  };

  const meta = [
    { icon: "ri-calendar-line", value: project.year, color: "#94A3B8" },
    ...(project.youtubeUrl ? [{ icon: "ri-youtube-line", value: "Video", color: "#F87171" }] : []),
    ...((project.galleryIds?.length ?? 0) > 0 ? [{ icon: "ri-image-line", value: `${project.galleryIds?.length} foto`, color: "#60A5FA" }] : []),
  ];

  const completeness = Math.round(
    ([project.name, project.location, project.year, project.area, project.description?.trim().length > 0 ? "x" : "", (project.features?.length || 0) > 0 ? "x" : "", (project.imageId || project.image) ? "x" : ""].filter(Boolean).length / 7) * 100
  );

  return (
    <InspectorCard
      label={project.name}
      description={`${project.location} · ${project.type}`}
      tab="projects"
      itemId={project.id}
      borderColor={tagInfo?.color || colors.projects.border}
      bgColor={colors.projects.bg}
      badgeBg={tagInfo?.color || colors.projects.badge}
      badgeText={colors.projects.text}
      icon="ri-building-2-line"
      inlineEditFields={inlineFields}
      onInlineSave={handleInlineSave}
      meta={meta}
      completeness={completeness}
    >
      <div className="bg-white rounded-xl overflow-hidden border border-gray-100">
        <div className="relative h-48 overflow-hidden">
          <img src={imgUrl} alt={project.name} className="w-full h-full object-cover object-top" />
          <div className="absolute top-3 left-3">
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full text-white" style={{ background: tagInfo?.color || "#94A3B8" }}>
              {project.status}
            </span>
          </div>
          {(project.galleryIds?.length ?? 0) > 0 && (
            <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
              <i className="ri-image-line text-xs"></i>
              <span>{project.galleryIds?.length}</span>
            </div>
          )}
          {completeness < 100 && (
            <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-black/30">
              <div className="h-full" style={{ width: `${completeness}%`, background: completeness < 60 ? "#F43F5E" : "#F59E0B" }}></div>
            </div>
          )}
        </div>
        <div className="p-4">
          <h3 className="font-bold text-gray-900 text-base leading-tight">{project.name}</h3>
          <p className="text-gray-500 text-sm mt-1 flex items-center gap-1">
            <i className="ri-map-pin-line text-xs"></i>
            {project.location}
          </p>
          <div className="flex gap-4 mt-3 text-xs text-gray-400 flex-wrap">
            <span><i className="ri-calendar-line mr-1"></i>{project.year}</span>
            <span><i className="ri-ruler-line mr-1"></i>{project.area}</span>
            <span><i className="ri-home-3-line mr-1"></i>{project.type}</span>
          </div>
          {project.features.length > 0 && (
            <div className="flex gap-1.5 mt-3 flex-wrap">
              {project.features.slice(0, 3).map((f, i) => (
                <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{f}</span>
              ))}
              {project.features.length > 3 && (
                <span className="text-xs text-gray-400">+{project.features.length - 3}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </InspectorCard>
  );
}

function InspectorProjectCards() {
  const [tick, setTick] = useState(0);
  const projects = siteData.getProjects();
  const activeProjects = projects.filter(p => !p.archived);
  const archivedProjects = projects.filter(p => p.archived);
  const [showArchived, setShowArchived] = useState(false);
  const displayProjects = showArchived ? projects : activeProjects;

  return (
    <div className="bg-[#111928] py-10 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-white">Tüm Projeler</h2>
            <p className="text-[#7B8FAF] text-sm mt-0.5">
              {activeProjects.length} aktif
              {archivedProjects.length > 0 && <span className="text-orange-400 ml-2">{archivedProjects.length} arşivde</span>}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {archivedProjects.length > 0 && (
              <button onClick={() => setShowArchived(p => !p)}
                className="flex items-center gap-2 px-3 py-1.5 bg-[#1E2B4A] border border-[#2B3654] text-[#7B8FAF] text-xs rounded-lg hover:text-white cursor-pointer transition-colors whitespace-nowrap">
                <i className={showArchived ? "ri-eye-off-line" : "ri-archive-line"}></i>
                {showArchived ? "Arşivi Gizle" : `Arşivi Göster (${archivedProjects.length})`}
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" key={tick}>
          {displayProjects.map(p => (
            <InspectorProjectCard key={p.id} project={p} onUpdated={() => setTick(t => t + 1)} />
          ))}
        </div>
        {displayProjects.length === 0 && (
          <div className="text-center py-20">
            <i className="ri-inbox-line text-4xl text-[#3B82F6]"></i>
            <p className="text-[#7B8FAF] mt-3">Proje bulunamadı</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Individual Partner Card ───────────────────────────────────────────────────

function InspectorPartnerCard({
  partner, onUpdated,
}: {
  partner: ReturnType<typeof siteData.getPartners>[0];
  onUpdated: () => void;
}) {
  const imgUrl = useImageUrl(partner.heroImageId, partner.heroImage);

  const inlineFields: InlineField[] = [
    { key: "name",      label: "Ortak Adı",     value: partner.name },
    { key: "sector",    label: "Sektör",         value: partner.sector },
    { key: "founded",   label: "Kuruluş Yılı",   value: partner.founded },
    { key: "since",     label: "Ortaklık Yılı",  value: partner.since },
    { key: "shortDesc", label: "Kısa Açıklama",  value: partner.shortDesc, multiline: true },
    { key: "fullDesc",  label: "Tam Açıklama",   value: partner.fullDesc, multiline: true },
    { key: "website",   label: "Web Sitesi",     value: partner.website },
    { key: "projects",  label: "Proje Sayısı",   value: String(partner.projects) },
    { key: "tag0",      label: "Etiket 1",       value: partner.tags[0] || "" },
    { key: "tag1",      label: "Etiket 2",       value: partner.tags[1] || "" },
    { key: "tag2",      label: "Etiket 3",       value: partner.tags[2] || "" },
  ];

  const handleInlineSave = (fields: Record<string, string>) => {
    const all = siteData.getPartners();
    const updated = all.map(p => p.id === partner.id ? {
      ...p, ...fields,
      projects: Number(fields.projects || p.projects),
      tags: [fields.tag0 || "", fields.tag1 || "", fields.tag2 || ""].filter(Boolean),
    } : p);
    siteData.savePartners(updated, "Inline Düzenlendi", partner.name + " - Inspector");
    onUpdated();
  };

  const meta = [
    { icon: "ri-links-line", value: partner.sector, color: "#94A3B8" },
    { icon: "ri-bar-chart-line", value: `${partner.projects} proje`, color: "#60A5FA" },
    { icon: "ri-calendar-line", value: `${partner.since}'den beri`, color: "#A78BFA" },
  ];

  const completeness = Math.round(
    ([partner.name, partner.sector, partner.shortDesc, partner.website, partner.heroImage || partner.heroImageId].filter(Boolean).length / 5) * 100
  );

  return (
    <InspectorCard
      label={partner.name}
      description={partner.sector}
      tab="partners"
      itemId={partner.id}
      borderColor={colors.partners.border}
      bgColor={colors.partners.bg}
      badgeBg={colors.partners.badge}
      badgeText={colors.partners.text}
      icon="ri-links-line"
      inlineEditFields={inlineFields}
      onInlineSave={handleInlineSave}
      meta={meta}
      completeness={completeness}
    >
      <div className="bg-white rounded-xl overflow-hidden border border-gray-100">
        <div className="h-36 overflow-hidden relative">
          <img src={imgUrl} alt={partner.name} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
          <div className="absolute bottom-3 left-4 right-4">
            <h3 className="text-white font-bold text-sm">{partner.name}</h3>
            <p className="text-white/70 text-xs mt-0.5">{partner.sector}</p>
          </div>
        </div>
        <div className="p-4">
          <p className="text-gray-600 text-xs mb-3 line-clamp-2">{partner.shortDesc}</p>
          <div className="flex gap-4 flex-wrap">
            {partner.stats.slice(0, 3).map((s, i) => (
              <div key={i} className="text-center">
                <p className="text-gray-900 font-bold text-sm">{s.value}</p>
                <p className="text-gray-400 text-xs">{s.label}</p>
              </div>
            ))}
          </div>
          {partner.tags.length > 0 && (
            <div className="flex gap-1.5 mt-3 flex-wrap">
              {partner.tags.slice(0, 3).map((t, i) => (
                <span key={i} className="text-xs bg-rose-50 text-rose-600 px-2 py-0.5 rounded-full">{t}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </InspectorCard>
  );
}

function InspectorPartnerCards() {
  const [tick, setTick] = useState(0);
  const partners = siteData.getPartners();
  return (
    <div className="bg-[#111928] py-10 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-white">Stratejik Ortaklar</h2>
            <p className="text-[#7B8FAF] text-sm mt-0.5">{partners.length} ortak kayıtlı</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" key={tick}>
          {partners.map(p => (
            <InspectorPartnerCard key={p.id} partner={p} onUpdated={() => setTick(t => t + 1)} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Help Panel ────────────────────────────────────────────────────────────────

function HelpPanel({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed right-0 top-14 bottom-0 w-80 bg-[#0F1729] border-l border-[#2B3654] z-50 overflow-y-auto">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <p className="text-white font-semibold">İnspektör Kullanımı</p>
          <button onClick={onClose} className="text-[#7B8FAF] hover:text-white cursor-pointer w-7 h-7 flex items-center justify-center">
            <i className="ri-close-line text-base"></i>
          </button>
        </div>
        <div className="space-y-4">
          {[
            { title: "Bölüme Hover Yap", icon: "ri-cursor-line", color: "#10B981", desc: "Herhangi bir bölümün üzerine gel — renkli çerçeve ve butonlar belirer." },
            { title: "Inline Düzenle (Yeşil)", icon: "ri-pencil-fill", color: "#10B981", desc: "Tüm metin alanlarını (başlık, açıklama, buton yazıları...) anında değiştir. Kaydet tuşuna bas." },
            { title: "Tam Editör (Renkli)", icon: "ri-settings-3-line", color: "#F59E0B", desc: "Admin panelini açar — görsel yükleme dahil tüm özellikler." },
            { title: "Detaylar (Siyah)", icon: "ri-information-line", color: "#3B82F6", desc: "Bölüm meta bilgisi: alan listesi, boyut, son değişiklik tarihi." },
            { title: "Proje / Ortak Kartları", icon: "ri-building-2-line", color: "#F43F5E", desc: "Her karta ayrı inline editör — ad, konum, açıklama, özellikler dahil." },
            { title: "Doluluk Çubuğu", icon: "ri-bar-chart-fill", color: "#F43F5E", desc: "Her kartın altındaki renkli çubuk içerik doluluk yüzdesini gösterir." },
          ].map((item, i) => (
            <div key={i} className="flex gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: item.color + "20" }}>
                <i className={`${item.icon} text-sm`} style={{ color: item.color }}></i>
              </div>
              <div>
                <p className="text-white text-xs font-medium">{item.title}</p>
                <p className="text-[#7B8FAF] text-xs mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5 bg-[#141E35] border border-[#2B3654] rounded-xl p-4">
          <p className="text-amber-400 text-xs font-semibold mb-2"><i className="ri-information-line mr-1"></i>Not</p>
          <p className="text-[#7B8FAF] text-xs">Inline düzenleme sonrası sayfayı yenilemeniz gerekebilir (Ctrl+R veya Cmd+R) değişikliklerin görseller de dahil yansıması için.</p>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function SiteInspector() {
  const [activePage, setActivePage] = useState<InspectorPage>("home");
  const [showHelp, setShowHelp] = useState(false);
  const navigate = useNavigate();

  const pageConfigs = buildPageConfigs();
  const configs = pageConfigs[activePage];

  const goToAdmin = (tab: string) => navigate(`/kontrol-paneli?tab=${tab}`);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") navigate("/");
      if (e.key === "?" || (e.shiftKey && e.key === "/")) setShowHelp(p => !p);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate]);

  const lastChangedMap = siteData.getLastChanged();

  const getLastChangedForSection = (id: string): string | undefined => {
    const mapping: Record<string, string> = {
      hero: "yg_hero", features: "yg_features", "projects-p": "yg_projects",
      "partners-s": "yg_partners", testimon: "yg_testimonials", cta: "yg_cta",
      contact: "yg_cta", footer: "yg_about",
      "about-hero": "yg_about", "about-content": "yg_about", "about-cta": "yg_cta", "about-contact": "yg_cta",
      "proj-hero": "yg_projects", "proj-cards": "yg_projects",
      "part-hero": "yg_partners", "part-why": "yg_partners", "part-cards": "yg_partners",
    };
    const key = mapping[id];
    if (!key || !lastChangedMap[key]) return undefined;
    const d = new Date(lastChangedMap[key]);
    return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  const sectionComponents: Record<string, JSX.Element> = {
    hero:         <HeroSection />,
    features:     <FeaturesSection />,
    "projects-p": <ProjectsPreview />,
    "partners-s": <StrategicPartners />,
    testimon:     <TestimonialSection />,
    cta:          <CTASection />,
    contact:      <ContactSection />,
    footer:       <Footer />,
    "about-hero":    <AboutHero />,
    "about-content": <AboutContent />,
    "about-cta":     <CTASection />,
    "about-contact": <ContactSection />,
    "proj-hero":     <ProjectsHero />,
    "part-hero":     <PartnersHero />,
    "part-why":      <WhyPartners />,
  };

  const inspectorVisibility = getFeatureVisibility("site_inspector");

  if (inspectorVisibility !== "visible") {
    return <InspectorLockedPage />;
  }

  return (
    <div className="min-h-screen bg-[#0A1020] flex flex-col">
      {/* Top Toolbar */}
      <div className="sticky top-0 z-50 bg-[#0F1729] border-b border-[#2B3654] h-14 flex items-center gap-3 px-4">
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0 inline-block"></span>
            <span className="text-emerald-300 text-xs font-semibold whitespace-nowrap">Görsel İnpektör</span>
          </div>
        </div>

        {/* Page tabs */}
        <div className="flex-1 flex justify-center">
          <div className="flex gap-1 bg-[#182240] border border-[#2B3654] rounded-lg p-1">
            {pageTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActivePage(tab.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activePage === tab.id ? "bg-[#3B82F6] text-white" : "text-[#7B8FAF] hover:text-white hover:bg-white/5"
                }`}
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={`${tab.icon} text-xs`}></i>
                </div>
                <span className="hidden sm:inline">{tab.label}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${activePage === tab.id ? "bg-white/20 text-white" : "bg-[#2B3654] text-[#3B82F6]"}`}>
                  {tab.count()}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setShowHelp(p => !p)}
            className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs cursor-pointer transition-colors ${showHelp ? "bg-[#3B82F6] text-white" : "text-[#7B8FAF] hover:text-white hover:bg-white/5"}`}
            title="Yardım (?)"
          >
            <i className="ri-question-line text-sm"></i>
          </button>
          <button
            onClick={() => navigate("/kontrol-paneli")}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#3B82F6] text-white rounded-lg text-xs font-medium cursor-pointer hover:bg-[#3d4e6a] transition-colors whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-settings-3-line text-sm"></i>
            </div>
            <span className="hidden sm:inline">Kontrol Paneli</span>
          </button>
          <Link to="/" className="w-8 h-8 flex items-center justify-center text-[#7B8FAF] hover:text-white cursor-pointer hover:bg-white/5 rounded-lg transition-colors" title="Siteye Dön (Esc)">
            <i className="ri-close-line text-lg"></i>
          </Link>
        </div>
      </div>

      {/* Stats Bar */}
      <InspectorStatsBar activePage={activePage} />

      {/* Content */}
      <div className="flex flex-1 overflow-hidden">
        <SectionListPanel configs={configs} activePage={activePage} onSectionClick={goToAdmin} />

        <div className="flex-1 overflow-y-auto bg-[#111928]">
          {/* Hint bar */}
          <div className="bg-[#0F1729] border-b border-[#1E2B4A] px-5 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-cursor-line text-[#3B82F6] text-sm"></i>
              </div>
              <p className="text-[#7B8FAF] text-xs">
                Bölüm üzerine gel → <span className="text-emerald-400 font-medium">Inline Düzenle</span> (yazıları/metinleri anında değiştir) veya <span className="text-amber-400 font-medium">Tam Editör</span> (görseller dahil)
              </p>
            </div>
            <div className="hidden sm:flex items-center gap-3 text-xs text-[#465886] flex-shrink-0">
              <span><kbd className="bg-[#1E2B4A] px-1.5 py-0.5 rounded text-[#7B8FAF]">?</kbd> Yardım</span>
              <span><kbd className="bg-[#1E2B4A] px-1.5 py-0.5 rounded text-[#7B8FAF]">Esc</kbd> Çıkış</span>
            </div>
          </div>

          {/* HOME & ABOUT — use EditableSectionOverlay */}
          {(activePage === "home" || activePage === "about") && configs.map(cfg => (
            <EditableSectionOverlay
              key={cfg.id}
              cfg={cfg}
              lastChanged={getLastChangedForSection(cfg.id)}
            >
              {sectionComponents[cfg.id] || <div className="h-20 bg-[#141E35]" />}
            </EditableSectionOverlay>
          ))}

          {/* PROJECTS */}
          {activePage === "projects" && (
            <>
              <EditableSectionOverlay cfg={configs[0]} lastChanged={getLastChangedForSection(configs[0].id)}>
                <ProjectsHero />
              </EditableSectionOverlay>
              <InspectorProjectCards />
            </>
          )}

          {/* PARTNERS */}
          {activePage === "partners" && (
            <>
              {configs.slice(0, 2).map(cfg => (
                <EditableSectionOverlay key={cfg.id} cfg={cfg} lastChanged={getLastChangedForSection(cfg.id)}>
                  {sectionComponents[cfg.id] || <div className="h-20 bg-[#141E35]" />}
                </EditableSectionOverlay>
              ))}
              <InspectorPartnerCards />
            </>
          )}
        </div>

        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
      </div>
    </div>
  );
}
